import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';
import type { HarvestState } from '@/shared/api/generated/catalog';

import { useHarvest } from '../api/connections';

/* Six states, five distinct tones, and the distinctions are the point.
 *
 * `partial` is `warn`, not `ok`: a harvest that reached the source but could not read every object
 * produced a catalog that is incomplete in ways nobody has been told about, and the platform
 * deliberately refuses to infer absence from it. Showing it in green would erase exactly the fact the
 * state exists to carry.
 *
 * `cancelled` is `neutral`, not `danger`: an operator stopping a harvest is a decision, not a fault,
 * and alerting on it trains people to ignore harvest alerts.
 */
const STATE_TONE: Readonly<Record<HarvestState, BadgeTone>> = {
  pending: 'info',
  running: 'info',
  succeeded: 'ok',
  partial: 'warn',
  failed: 'danger',
  cancelled: 'neutral',
};

export function HarvestProgress({
  harvestId,
  onDismiss,
}: {
  readonly harvestId: string;
  readonly onDismiss: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const harvest = useHarvest(harvestId);

  const operation = harvest.data;
  const done =
    operation !== undefined &&
    operation.state !== 'pending' &&
    operation.state !== 'running';

  return (
    <div
      /* `status`, not `alert`. Progress is informational and updates repeatedly; an assertive live
       * region would interrupt a screen-reader user on every poll. */
      role="status"
      aria-live="polite"
      className="lq-card"
      style={{ padding: 'var(--space-3) var(--space-4)' }}
    >
      <div className="lq-page-header" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <strong>{t('harvest.title')}</strong>
          {operation !== undefined && (
            <StatusBadge
              tone={STATE_TONE[operation.state]}
              label={t(`harvest.state.${operation.state}.label`)}
              title={t(`harvest.state.${operation.state}.meaning`)}
            />
          )}
        </div>
        {done && <Button onClick={onDismiss}>{t('harvest.dismiss')}</Button>}
      </div>

      {harvest.isError && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <Alert tone="error">{harvest.error.message}</Alert>
        </div>
      )}

      {operation !== undefined && (
        <>
          <p
            className="lumiq-numeric"
            style={{
              margin: 'var(--space-2) 0 0',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            {t('harvest.progress', {
              discovered: operation.progress.discovered,
              observed: operation.progress.observed,
              failed: operation.progress.failed,
            })}
          </p>

          {operation.absence_withheld > 0 && (
            /* The fact that makes `partial` actionable. "Your catalog may be stale by N datasets" is
             * what an operator needs in order to decide whether to re-run — and it is reported even
             * when zero, so a client never has to distinguish "nothing withheld" from "not reported". */
            <p
              style={{
                margin: 'var(--space-2) 0 0',
                color: 'var(--status-warn)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {t('harvest.absenceWithheld', { count: operation.absence_withheld })}
            </p>
          )}

          {operation.detail !== null && (
            <p
              style={{
                margin: 'var(--space-2) 0 0',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-secondary)',
              }}
            >
              {operation.detail}
            </p>
          )}

          {operation.failures.length > 0 && (
            <details style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--status-warn)' }}>
                {t('harvest.failures', { count: operation.failures.length })}
              </summary>
              <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-4)' }}>
                {operation.failures.map((failure) => (
                  <li key={failure.qualified_name}>
                    <span className="lumiq-numeric">{failure.qualified_name}</span>
                    {' — '}
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {failure.reason}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
